import { Link, useSearch } from '@tanstack/react-router';
import { FileIcon, Trash2Icon } from 'lucide-react';
import { useState } from 'react';

import { Button } from '@/components/ui/button';
import { useWorkspaceFilePreviewLink } from '@/features/workspace-files';
import { formatFileSize } from '@/features/workspace-files/format';
import { OverwriteConfirmDialog } from '@/features/workspace-files/components/OverwriteConfirmDialog';
import { usePendingDisplay } from '@/shared/hooks/usePendingDisplay';
import { taskQueueId } from '../drafts/taskDraftStore';
import { useAttachmentUploads } from '../hooks/useAttachmentUploads';
import { useRemoveTaskAttachment } from '../hooks/useRemoveTaskAttachment';
import { useTaskAttachments } from '../hooks/useTaskAttachments';
import { AttachmentPicker } from './AttachmentPicker';
import { StagedAttachments } from './StagedAttachments';
import { SkeletonRows } from '@/shared/components/PendingRows';
import { useCanWork } from '@/features/workspaces/canWork';

/**
 * A task's attachments, in its drawer (spec 120 FR-006 – FR-009): the files in
 * its attachments folder, each one click from a look at it, with a way to add
 * more and to remove one.
 *
 * Clicking an attachment opens the preview panel beside the sheet rather than
 * navigating to the Files page (spec 140 FR-052): the destination comes from
 * `useWorkspaceFilePreviewLink`, the one place that decision is made, so this
 * row gets the sub-`sm` fallback and the push-versus-replace rule without
 * holding a copy of either. It was a navigation away from the task someone was
 * reading — the reviewer revisited that before the panel shipped (D1).
 *
 * Read once when the drawer shows the task, never polled: the list changes
 * when someone here adds or removes a file, and those invalidate it. Uploads
 * go through the same queue the Create dialog uses, already pointed at the
 * task. A name that is already attached asks first — Replace, Keep both (the
 * service numbers the copy) or Skip — one file at a time.
 */
export function TaskAttachmentsSection({
  workspaceId,
  taskId,
}: {
  workspaceId: string;
  taskId: string;
}) {
  const listed = useTaskAttachments(workspaceId, taskId);
  /*
    The file already previewed beside this task, read from the address rather
    than taken as a prop: this component sits four levels under the drawer and
    the param it needs is the one the drawer itself reads, so threading it
    would add `previewPath` to `TaskDetailsPanel` for a single row's
    push-versus-replace decision. `strict: false` because the attachments
    section renders on three routes (board, Tasks, run), exactly as
    `CommandPaletteHost` reads `?task=`.
  */
  const search: { preview?: unknown } = useSearch({ strict: false });
  const previewPath =
    typeof search.preview === 'string' ? search.preview : undefined;
  const previewLink = useWorkspaceFilePreviewLink(workspaceId, previewPath);
  const uploads = useAttachmentUploads(workspaceId, taskQueueId(taskId));
  const remove = useRemoveTaskAttachment(workspaceId, taskId);
  const [clashes, setClashes] = useState<File[]>([]);
  const [removing, setRemoving] = useState<string | null>(null);
  const showPlaceholder = usePendingDisplay(listed.isPending);
  // Someone only reading the workspace sees and opens them, nothing more (spec 189).
  const canWork = useCanWork();

  const add = (files: File[]) => {
    uploads.startFor(taskId);
    const taken = new Set(listed.attachments.map((a) => a.name));
    const clear = files.filter((file) => !taken.has(file.name));
    if (clear.length > 0) uploads.add(clear);
    setClashes((queue) => [
      ...queue,
      ...files.filter((file) => taken.has(file.name)),
    ]);
  };
  const [clash] = clashes;
  const settleClash = (choice: 'replace' | 'keep' | 'skip') => {
    if (clash && choice !== 'skip') {
      uploads.add([clash], { replace: choice === 'replace' });
    }
    setClashes((queue) => queue.slice(1));
  };

  if (listed.isPending) {
    return showPlaceholder ? (
      <SkeletonRows label="Loading attachments" rows={2} />
    ) : null;
  }
  if (listed.loadFailed) {
    return (
      <div className="flex flex-col items-start gap-2">
        <p className="text-muted-foreground text-sm">
          Attachments could not be loaded.
        </p>
        <Button variant="outline" size="sm" onClick={listed.retry}>
          Try again
        </Button>
      </div>
    );
  }

  const inFlight = uploads.items.filter((item) => item.status.kind !== 'done');
  return (
    <div className="flex flex-col gap-2">
      {listed.attachments.length > 0 ? (
        <ul aria-label="Attachments" className="flex flex-col gap-1">
          {listed.attachments.map((attachment) => (
            <li
              key={attachment.name}
              className="flex min-w-0 flex-wrap items-center gap-x-2 text-sm"
            >
              <FileIcon
                aria-hidden="true"
                className="text-muted-foreground size-4 shrink-0"
              />
              <Link
                {...previewLink(attachment.path)}
                className="text-primary min-w-0 underline underline-offset-2 [overflow-wrap:anywhere]"
              >
                {attachment.name}
              </Link>
              <span className="text-muted-foreground text-xs">
                {formatFileSize(attachment.size)}
              </span>
              {!canWork ? null : removing === attachment.name ? (
                <span className="ml-auto flex items-center gap-1">
                  <span className="text-xs">Remove from Files?</span>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => {
                      remove.mutate(attachment.name);
                      setRemoving(null);
                    }}
                  >
                    Remove
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setRemoving(null);
                    }}
                  >
                    Keep
                  </Button>
                </span>
              ) : (
                <Button
                  variant="ghost"
                  size="icon-sm"
                  // Quiet (spec 181): as bold text it was the loudest thing in
                  // the section. The question that follows still asks.
                  className="text-muted-foreground hover:text-destructive ml-auto"
                  aria-label={`Remove ${attachment.name}`}
                  title={`Remove ${attachment.name}`}
                  onClick={() => {
                    setRemoving(attachment.name);
                  }}
                >
                  <Trash2Icon aria-hidden="true" strokeWidth={1.75} />
                </Button>
              )}
            </li>
          ))}
        </ul>
      ) : null}
      {remove.isError ? (
        <p role="alert" className="text-destructive text-xs">
          {remove.error.message}
        </p>
      ) : null}
      <StagedAttachments
        items={inFlight}
        onRemove={uploads.remove}
        onRetry={uploads.retry}
      />
      {canWork ? (
        <AttachmentPicker
          onFiles={add}
          label="Add attachment"
          compact={listed.attachments.length > 0}
        />
      ) : listed.attachments.length === 0 ? (
        <p className="text-muted-foreground text-sm">No attachments.</p>
      ) : null}
      <OverwriteConfirmDialog
        open={clash !== undefined}
        fileName={clash?.name ?? ''}
        where="in this task's attachments"
        onConfirm={() => {
          settleClash('replace');
        }}
        onKeepBoth={() => {
          settleClash('keep');
        }}
        onCancel={() => {
          settleClash('skip');
        }}
      />
    </div>
  );
}
